import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db } from '../core/firebase/firebase';
import { useAuth } from '../core/firebase/useAuth';
import { useAuthModal } from '../core/AuthModalContext';
import { normalizeCategory } from '../core/categoryUtils';
import { trackLike } from '../core/taste';
import { IconHeart } from './Icons';
import TrendingBadge from './TrendingBadge';
import WishlistButton from './WishlistButton';

function ProductCard({ product }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAuthModal } = useAuthModal();

  const likedBy = product.likedBy || [];
  const [liked,  setLiked]  = useState(user ? likedBy.includes(user.uid) : false);
  const [likes,  setLikes]  = useState(product.likes || 0);
  const [saving, setSaving] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) { showAuthModal(); return; }
    if (saving) return;
    setSaving(true);
    const ref = doc(db, product._col || 'ebay_products', product.id);
    try {
      if (liked) {
        await updateDoc(ref, { likedBy: arrayRemove(user.uid), likes: increment(-1) });
        setLiked(false); setLikes(l => l - 1);
      } else {
        await updateDoc(ref, { likedBy: arrayUnion(user.uid), likes: increment(1) });
        setLiked(true); setLikes(l => l + 1);
        trackLike(normalizeCategory(product.category));
      }
    } catch {}
    finally { setSaving(false); }
  };

  const openDetail = () => {
    const col = product._col || 'ebay_products';
    navigate(`/product/${col}/${product.id}`, { state: { product } });
  };

  const image = (!imgErr && (product.imageUrl || product.image)) || null;
  const price = product.price ? `$${parseFloat(product.price).toFixed(0)}` : null;
  const title = product.title || '';
  const rating = product.rating || 4.5;
  const reviews = product.reviews || Math.floor(Math.random() * 100);

  return (
    <div 
      className="product-card" 
      onClick={openDetail}
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeInUp 0.4s ease-out',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
        e.currentTarget.style.borderColor = '#e0e7ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--border-color)';
      }}
    >
      {/* ── Image ── */}
      <div style={{
        width: '100%',
        aspectRatio: '1',
        background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {image ? (
          <img
            src={image}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.4s ease',
            }}
            referrerPolicy="no-referrer"
            onError={() => setImgErr(true)}
            onMouseEnter={(e) => (e.target.style.transform = 'scale(1.08)')}
            onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '28px', opacity: 0.4 }}>📷</span>
            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '500' }}>No image</span>
          </div>
        )}

        {/* Badge Creator */}
        {product.isCreator && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: '#fff',
            fontSize: '11px',
            fontWeight: '700',
            borderRadius: '20px',
            padding: '4px 10px',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            zIndex: 2,
          }}>
            ✦ Creator
          </div>
        )}

        {/* Like Button */}
        <button
          onClick={handleLike}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: liked ? 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)' : 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 2,
          }}
          onMouseEnter={(e) => {
            if (!liked) e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          <IconHeart size={16} filled={liked} color={liked ? '#fff' : '#ec4899'} />
        </button>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <p style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          margin: '0 0 8px 0',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {title}
        </p>

        {/* Meta Info */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>⭐</span>
            <span>{rating.toFixed(1)}</span>
          </div>
          {reviews > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>💬</span>
              <span>{reviews}</span>
            </div>
          )}
        </div>

        {/* Price */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: 'auto', marginBottom: '12px' }}>
          {price && (
            <>
              <span style={{
                fontSize: '16px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {price}
              </span>
              {product.originalPrice && (
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>
                  ${product.originalPrice}
                </span>
              )}
            </>
          )}
          {likes > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#ec4899', fontWeight: '600' }}>
              ♥ {likes}
            </span>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={(e) => { e.stopPropagation(); openDetail(); }}
          style={{
            width: '100%',
            padding: '10px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.02)';
            e.target.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
          }}
        >
          Voir
        </button>
      </div>
    </div>
  );
}

export default React.memo(ProductCard, (prev, next) =>
  prev.product.id    === next.product.id &&
  prev.product._col  === next.product._col &&
  prev.product.likes === next.product.likes
);
